import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { getEmpleadosAsesores, getGestionCartera, reasignarAsesorCartera } from '../../../js/gestionCarteraService';
import GestionCarteraFilters from '../../../components/ui/Admin/GestionCarteraComponents/GestionCarteraFilters';
import GestionCarteraBulkPanel from '../../../components/ui/Admin/GestionCarteraComponents/GestionCarteraBulkPanel';
import GestionCarteraTable from '../../../components/ui/Admin/GestionCarteraComponents/GestionCarteraTable';
import Paginacion from '../../../components/Reutilizables/Paginacion';
import FetchWithGif from '../../../components/Reutilizables/FetchWithGif';
import {
  EmptyState,
  ErrorState,
} from '../../../components/ui/Admin/DashboardComponents/LoadingSkeletons/SkeletonKPICard';
import {
  GestionCarteraSkeletonBulkPanel,
  GestionCarteraSkeletonFilters,
  GestionCarteraSkeletonTable,
} from '../../../components/ui/Admin/GestionCarteraComponents/GestionCarteraSkeletons';

const MAX_SELECTION = 500;

const INITIAL_FILTERS = {
  search: '',
  asesor_id: '',
  per_page: 50,
  page: 1,
};

const INITIAL_STATE = {
  loading: false,
  error: '',
  data: [],
  meta: {},
  initialLoad: true,
};

function mapBackendErrors(payload = {}) {
  const backendErrors = payload?.errors || {};

  return Object.keys(backendErrors).reduce((acc, key) => {
    if (backendErrors[key]?.length) {
      acc[key] = backendErrors[key][0];
    }

    return acc;
  }, {});
}

function isAsesorActivo(asesor) {
  return Number(asesor?.estado) === 1 || String(asesor?.estado || '').toLowerCase() === 'activo';
}

function normalizeAsesor(asesor) {
  const id = asesor?.id ?? asesor?.idUsuario;
  const datos = asesor?.datos || {};
  const nombre = asesor?.nombre_completo
    || asesor?.nombre
    || [datos.nombre, datos.apellidoPaterno, datos.apellidoMaterno].filter(Boolean).join(' ')
    || `Asesor ${id}`;

  return {
    id,
    nombre,
    idRol: Number(asesor?.idRol),
    activo: isAsesorActivo(asesor),
  };
}

export default function GestionCarteraUI() {
  const hasLoadedRef = useRef(false);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [queryFilters, setQueryFilters] = useState(INITIAL_FILTERS);
  const [asesoresFiltro, setAsesoresFiltro] = useState([]);
  const [asesoresActivos, setAsesoresActivos] = useState([]);
  const [state, setState] = useState(INITIAL_STATE);
  const [filterErrors, setFilterErrors] = useState({});
  const [bulkErrors, setBulkErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [selectedById, setSelectedById] = useState({});
  const [asesorDestinoId, setAsesorDestinoId] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const rows = useMemo(() => (Array.isArray(state.data) ? state.data : []), [state.data]);
  const meta = useMemo(() => state.meta || {}, [state.meta]);
  const selectedRows = useMemo(() => Object.values(selectedById), [selectedById]);
  const selectedCount = selectedRows.length;
  const selectedIds = useMemo(() => new Set(Object.keys(selectedById).map(Number)), [selectedById]);
  const hasData = rows.length > 0;
  const isInitialLoading = state.loading && state.initialLoad && !hasData;
  const hasFatalError = Boolean(state.error) && !hasData;

  useEffect(() => {
    let cancelled = false;

    async function loadAsesores() {
      try {
        const result = await getEmpleadosAsesores();
        const raw = result.asesores || result.data || [];
        const normalized = raw
          .map(normalizeAsesor)
          .filter((asesor) => asesor.id && asesor.idRol === 3);

        if (!cancelled) {
          setAsesoresFiltro(normalized);
          setAsesoresActivos(normalized.filter((asesor) => asesor.activo));
        }
      } catch (error) {
        if (!cancelled) {
          toast.error(error?.message || 'No se pudo cargar la lista de asesores.');
        }
      }
    }

    loadAsesores();

    return () => {
      cancelled = true;
    };
  }, []);

  const fetchData = useCallback(async (isInitialLoad = false) => {
    setState((prevState) => ({
      ...prevState,
      loading: true,
      initialLoad: isInitialLoad,
    }));
    setFormError('');

    try {
      const result = await getGestionCartera(queryFilters);

      setState({
        loading: false,
        error: '',
        data: Array.isArray(result?.data) ? result.data : [],
        meta: result?.meta || {},
        initialLoad: false,
      });
      setFilterErrors({});
    } catch (error) {
      const status = error?.status || error?.response?.status;
      const payload = error?.response?.data || {};

      if (status === 422) {
        setFilterErrors(mapBackendErrors(payload));
        setFormError(payload?.message || 'Revise los campos marcados.');
        setState((prevState) => ({
          ...prevState,
          loading: false,
          initialLoad: false,
        }));
        return;
      }

      setState((prevState) => ({
        ...prevState,
        loading: false,
        error: error?.message || 'No se pudo cargar la gestión de cartera.',
        initialLoad: false,
      }));
    }
  }, [queryFilters]);

  useEffect(() => {
    const isFirstLoad = !hasLoadedRef.current;
    hasLoadedRef.current = true;
    fetchData(isFirstLoad);
  }, [fetchData]);

  const clearSelection = useCallback(() => {
    setSelectedById({});
    setAsesorDestinoId('');
    setBulkErrors({});
  }, []);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
    setFilterErrors((prev) => ({ ...prev, [name]: '' }));
  }, []);

  const handleApplyFilters = useCallback(() => {
    clearSelection();
    setFormError('');
    setFilterErrors({});
    setQueryFilters({ ...filters, page: 1 });
  }, [clearSelection, filters]);

  const handleResetFilters = useCallback(() => {
    clearSelection();
    setFilters(INITIAL_FILTERS);
    setQueryFilters(INITIAL_FILTERS);
    setFormError('');
    setFilterErrors({});
  }, [clearSelection]);

  const handlePageChange = useCallback((newPage) => {
    setQueryFilters((prev) => ({ ...prev, page: newPage }));
  }, []);

  const handleToggleRow = useCallback((row) => {
    const id = Number(row.id_prestamo);

    setSelectedById((prev) => {
      if (prev[id]) {
        const next = { ...prev };
        delete next[id];
        return next;
      }

      if (Object.keys(prev).length >= MAX_SELECTION) {
        toast.warning(`Solo se pueden seleccionar hasta ${MAX_SELECTION} créditos por operación.`);
        return prev;
      }

      return { ...prev, [id]: row };
    });
    setBulkErrors({});
  }, []);

  const handleTogglePage = useCallback((visibleRows, allPageSelected) => {
    setSelectedById((prev) => {
      const next = { ...prev };

      if (allPageSelected) {
        visibleRows.forEach((row) => {
          delete next[Number(row.id_prestamo)];
        });
        return next;
      }

      let availableSlots = MAX_SELECTION - Object.keys(next).length;
      let truncated = false;

      visibleRows.forEach((row) => {
        const id = Number(row.id_prestamo);
        if (next[id]) return;

        if (availableSlots <= 0) {
          truncated = true;
          return;
        }

        next[id] = row;
        availableSlots -= 1;
      });

      if (truncated) {
        toast.warning(`Selección parcial: backend permite hasta ${MAX_SELECTION} créditos por operación.`);
      }

      return next;
    });
    setBulkErrors({});
  }, []);

  const handleRequestSubmit = useCallback(() => {
    if (selectedCount === 0) {
      toast.warning('Debe seleccionar al menos un crédito.');
      return;
    }

    if (!asesorDestinoId) {
      setBulkErrors({ id_asesor_destino: 'Debe seleccionar un asesor destino.' });
      return;
    }

    const allAlreadyAssigned = selectedRows.every(
      (row) => String(row.asesor?.id || '') === String(asesorDestinoId)
    );

    if (allAlreadyAssigned) {
      setBulkErrors({ id_asesor_destino: 'Los créditos seleccionados ya pertenecen a ese asesor.' });
      return;
    }

    setBulkErrors({});
    setConfirmOpen(true);
  }, [asesorDestinoId, selectedCount, selectedRows]);

  const handleConfirmSubmit = useCallback(async () => {
    setSubmitting(true);

    try {
      const result = await reasignarAsesorCartera({
        asesorDestinoId,
        prestamosIds: selectedRows.map((row) => row.id_prestamo),
      });

      toast.success(result?.message || 'Créditos reasignados con éxito.');
      setConfirmOpen(false);
      clearSelection();
      await fetchData(false);
    } catch (error) {
      const status = error?.status || error?.response?.status;
      const payload = error?.response?.data || {};

      if (status === 422) {
        setBulkErrors(mapBackendErrors(payload));
        toast.error(payload?.message || 'Revise los datos de reasignación.');
      } else {
        toast.error(error?.message || 'No se pudo reasignar la cartera.');
      }
    } finally {
      setSubmitting(false);
    }
  }, [asesorDestinoId, clearSelection, fetchData, selectedRows]);

  const asesorDestinoName = useMemo(() => {
    if (!asesorDestinoId) return '';
    return asesoresActivos.find((asesor) => String(asesor.id) === String(asesorDestinoId))?.nombre || asesorDestinoId;
  }, [asesorDestinoId, asesoresActivos]);

  const filterSummary = useMemo(() => {
    const parts = [];

    if (queryFilters.search) {
      parts.push(`Búsqueda: ${queryFilters.search}`);
    }

    if (queryFilters.asesor_id) {
      const asesor = asesoresFiltro.find((item) => String(item.id) === String(queryFilters.asesor_id));
      parts.push(`Asesor: ${asesor?.nombre || queryFilters.asesor_id}`);
    }

    return parts.join(' · ');
  }, [queryFilters, asesoresFiltro]);

  if (isInitialLoading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
          <header className="mb-6">
            <div className="mb-2 h-8 w-80 animate-pulse rounded bg-slate-200" />
            <div className="h-4 w-64 animate-pulse rounded bg-slate-200" />
          </header>
          <GestionCarteraSkeletonFilters />
          <GestionCarteraSkeletonBulkPanel />
          <GestionCarteraSkeletonTable />
        </div>
      </div>
    );
  }

  if (hasFatalError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <ErrorState title="Error al cargar gestión de cartera" message={state.error} onRetry={() => fetchData(true)} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {state.loading && !state.initialLoad ? <FetchWithGif /> : null}

      <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
        <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
          <div className="flex flex-col gap-5 p-6 md:p-7">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.35em] text-red-500">Gestión de cartera</p>
                <h1 className="mt-2 text-2xl font-bold text-slate-900">Créditos activos por asesor</h1>
                <p className="mt-3 max-w-2xl text-sm text-slate-600">
                  Selecciona créditos activos y transfiere su asesor asignado
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
          <GestionCarteraFilters
            filters={filters}
            asesores={asesoresFiltro}
            loading={state.loading}
            errors={filterErrors}
            formError={formError}
            onChange={handleFilterChange}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
          />
        </section>

        <GestionCarteraBulkPanel
          asesores={asesoresActivos}
          asesorDestinoId={asesorDestinoId}
          selectedCount={selectedCount}
          maxSelection={MAX_SELECTION}
          submitting={submitting}
          errors={bulkErrors}
          onDestinoChange={(value) => {
            setAsesorDestinoId(value);
            setBulkErrors((prev) => ({ ...prev, id_asesor_destino: '' }));
          }}
          onSubmit={handleRequestSubmit}
          onClear={clearSelection}
        />

        {!hasData && !state.loading ? (
          <div className="rounded-3xl border border-dashed border-slate-200 bg-white p-8 shadow-sm">
            <EmptyState
              title="No hay créditos activos"
              description="No se encontraron créditos activos con los filtros seleccionados."
              actionButton={
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="rounded-lg bg-red-600 px-6 py-3 font-medium text-white transition hover:bg-red-700"
                >
                  Ver todos los créditos
                </button>
              }
            />
          </div>
        ) : (
          <section className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
            <GestionCarteraTable
              rows={rows}
              loading={state.loading}
              error={state.error}
              selectedIds={selectedIds}
              selectedCount={selectedCount}
              maxSelection={MAX_SELECTION}
              onToggleRow={handleToggleRow}
              onTogglePage={handleTogglePage}
            />
          </section>
        )}

        {meta.total > 0 && meta.last_page > 1 ? (
          <div className="rounded-3xl border border-slate-100 bg-white p-4 shadow-sm">
            <Paginacion
              paginacion={{
                pagina_actual: meta.current_page,
                ultima_pagina: meta.last_page,
                desde: meta.from,
                hasta: meta.to,
                total: meta.total,
              }}
              alCambiarPagina={handlePageChange}
              cargando={state.loading}
              nombreItem="créditos"
              colorTema="red"
            />
          </div>
        ) : null}

        {filterSummary ? (
          <div className="text-center text-sm text-slate-600">
            Filtros activos: <strong>{filterSummary}</strong>
          </div>
        ) : null}
      </div>

      {confirmOpen ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 p-4">
          <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl">
            <h3 className="text-lg font-semibold text-slate-900">Confirmar reasignación</h3>
            <p className="mt-2 text-sm text-slate-600">
              Se reasignarán {selectedCount} crédito{selectedCount === 1 ? '' : 's'} activo
              {selectedCount === 1 ? '' : 's'} al asesor <strong>{asesorDestinoName}</strong>.
            </p>
            <p className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              Esta acción cambia el asesor asociado al préstamo. Verifica selección y destino antes de continuar.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setConfirmOpen(false)}
                disabled={submitting}
                className="rounded-xl bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-300 disabled:cursor-not-allowed disabled:opacity-70"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleConfirmSubmit}
                disabled={submitting}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {submitting ? 'Procesando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}